import React, { useEffect, useRef } from "react";

function clamp01(v, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1, Math.max(0, n));
}
function clamp(v, lo, hi, fallback) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Living Orb — CTO Directive #81 + #111. A volumetric living energy
 * field; never a logo, never an icon. Under #111 the orb no longer
 * *guesses* what ME is doing — it renders the real Voice Stream
 * state and prosody facets (``energy``, ``tempo``, ``warmth``).
 *
 * Composition:
 *   - 4 nested radial energy shells (halo · corona · ring · plasma core)
 *   - rotating equatorial energy band (subtle, never aggressive)
 *   - orbital particle ring (22 particles, biological drift)
 *   - free-floating spec field (28 specks at varying depths)
 *   - inner plasma displacement (organic, offset by Lissajous curves)
 *
 * States: idle · listening · thinking · waiting · speaking · analysis · warning · critical.
 */
export default function LivingOrb({
  state = "idle",
  level = 0,
  size = 320,
  className = "",
  profile = null,
}) {
  const ref = useRef(null);
  const animRef = useRef(0);
  const startedAt = useRef(performance.now());
  const lvl = useRef(0);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Emotion palettes — never aggressive; calm always.
    const palettes = {
      idle:      { halo: "#3b82f618", corona: "#7cc4ff20", ring: "#a4d8ff66", core: "#e0f0ff", ring2: "#7cc4ff", breath: 1.0, contract: 0.0 },
      // CTO Directive #103 + #111 — passive listening palette.
      listening: { halo: "#22d3ee14", corona: "#67e8f91a", ring: "#a5f3fc66", core: "#ecfeff", ring2: "#67e8f9", breath: 1.0, contract: 0.0 },
      thinking:  { halo: "#3b82f628", corona: "#7cc4ff2c", ring: "#a4d8ff77", core: "#dde9ff", ring2: "#a4d8ff", breath: 2.2, contract: 0.15 },
      speaking:  { halo: "#f59e0b22", corona: "#fbbf2422", ring: "#fde68a77", core: "#fef9c3", ring2: "#fbbf24", breath: 2.6, contract: 0.05 },
      analysis:  { halo: "#1d4ed822", corona: "#2563eb2c", ring: "#7cc4ff77", core: "#dbeafe", ring2: "#3b82f6", breath: 1.8, contract: 0.20, geom: true },
      preparing: { halo: "#f59e0b1c", corona: "#fbbf2422", ring: "#fde68a66", core: "#fef9c3", ring2: "#fbbf24", breath: 1.7, contract: 0.18, geom: true },
      // CTO Directive #111 — silence is a real state. Waiting is
      // the space between two thoughts; the orb barely moves so the
      // user knows ME is present but not intruding.
      waiting:   { halo: "#3b82f610", corona: "#7cc4ff18", ring: "#a4d8ff44", core: "#e5efff", ring2: "#7cc4ff", breath: 0.42, contract: 0.05 },
      // CTO Directive #102.1 — ME consciously chose not to speak.
      observing: { halo: "#3b82f610", corona: "#7cc4ff14", ring: "#a4d8ff44", core: "#dbe6f5", ring2: "#7cc4ff", breath: 0.55, contract: 0.0 },
      // CTO Directive #111 — quiet is when the conversation has not
      // begun yet; the orb rests.
      quiet:     { halo: "#3b82f60c", corona: "#7cc4ff10", ring: "#a4d8ff33", core: "#e0eaff", ring2: "#7cc4ff", breath: 0.35, contract: 0.0 },
      // CTO Directive #126.3 — ME could not verify audio output and
      // is honestly continuing in writing. Muted, calm, low breath.
      written:   { halo: "#64748b14", corona: "#94a3b81a", ring: "#cbd5e155", core: "#e2e8f0", ring2: "#94a3b8", breath: 0.5, contract: 0.0 },
      warning:   { halo: "#f59e0b1c", corona: "#fbbf2426", ring: "#fde68a77", core: "#fef3c7", ring2: "#f59e0b", breath: 2.0, contract: 0.10 },
      critical:  { halo: "#dc26261c", corona: "#f8717126", ring: "#fca5a577", core: "#fee2e2", ring2: "#dc2626", breath: 2.4, contract: 0.10 },
    };
    // CTO Directive #111 — profile.energy/tempo/warmth reshape the
    // breath curve so the same "speaking" state can feel eager,
    // gentle, calm or urgent depending on Working Memory context.
    const basePalette = palettes[state] || palettes.idle;
    const energy = clamp01(profile?.energy, 0.55);
    const tempo = clamp(profile?.tempo, 0.6, 2.0, 1.0);
    const warmth = clamp01(profile?.warmth, 0.6);
    const p = {
      ...basePalette,
      breath: basePalette.breath * (0.65 + tempo * 0.55),
      contract: Math.max(0, basePalette.contract - (warmth - 0.5) * 0.25),
    };
    const audioBias = 0.35 + energy * 1.1;

    // Orbital ring particles
    const orbitN = 22;
    const orbital = Array.from({ length: orbitN }).map((_, i) => ({
      ph: (i / orbitN) * Math.PI * 2 + Math.random() * 0.2,
      rRel: 0.34 + (i % 5) * 0.018,
      sp: 0.10 + (i % 3) * 0.04,
      sz: 0.7 + (i % 4) * 0.25,
      depth: 0.4 + Math.random() * 0.6,
    }));
    // Free-floating specks (deeper, slower)
    const driftN = 28;
    const drift = Array.from({ length: driftN }).map(() => ({
      x: Math.random(),
      y: Math.random(),
      sz: 0.4 + Math.random() * 0.9,
      sp: 0.04 + Math.random() * 0.07,
      ph: Math.random() * Math.PI * 2,
      depth: 0.2 + Math.random() * 0.8,
    }));

    function draw(now) {
      lvl.current = lvl.current * 0.86 + level * 0.14;
      const audio = Math.min(1, lvl.current * audioBias);
      const t = (now - startedAt.current) / 1000;
      const cx = size / 2;
      const cy = size / 2;
      ctx.clearRect(0, 0, size, size);

      // Breath base curve — gentle 5-7s cycle
      const breath = 0.5 + 0.5 * Math.sin((t * p.breath) / 1.8);
      // Listening contracts particles inward
      const contract = p.contract;

      /* ---------- Shell 1: outer halo (volumetric atmosphere) ---------- */
      const haloR = size * 0.48 * (0.78 + 0.16 * breath + 0.18 * audio);
      const g1 = ctx.createRadialGradient(cx, cy, haloR * 0.05, cx, cy, haloR);
      g1.addColorStop(0, p.halo);
      g1.addColorStop(1, "transparent");
      ctx.fillStyle = g1;
      ctx.fillRect(0, 0, size, size);

      /* ---------- Shell 2: corona ---------- */
      const corR = size * 0.34 * (0.86 + 0.12 * breath + 0.2 * audio);
      const g2 = ctx.createRadialGradient(cx, cy, corR * 0.2, cx, cy, corR);
      g2.addColorStop(0, p.corona);
      g2.addColorStop(0.55, p.corona);
      g2.addColorStop(1, "transparent");
      ctx.fillStyle = g2;
      ctx.beginPath();
      ctx.arc(cx, cy, corR, 0, Math.PI * 2);
      ctx.fill();

      /* ---------- Rotating equatorial energy band ---------- */
      const ringR = size * 0.22 * (1 + 0.05 * breath);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(t * 0.18);
      ctx.scale(1, 0.36);
      const gRing = ctx.createRadialGradient(0, 0, ringR * 0.6, 0, 0, ringR);
      gRing.addColorStop(0, "transparent");
      gRing.addColorStop(0.8, p.ring);
      gRing.addColorStop(1, "transparent");
      ctx.strokeStyle = gRing;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.arc(0, 0, ringR, 0, Math.PI * 2);
      ctx.stroke();
      // second counter-rotating band
      ctx.rotate(-t * 0.42);
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(0, 0, ringR * 0.78, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      /* ---------- Shell 3: inner plasma core ---------- */
      const offX = Math.sin(t * 1.1) * 1.4 + Math.cos(t * 0.63) * 1.0;
      const offY = Math.cos(t * 0.9) * 1.1 + Math.sin(t * 0.52) * 0.9;
      const coreR = size * 0.10 * (1 + 0.12 * breath + 0.6 * audio);
      const g3 = ctx.createRadialGradient(
        cx + offX, cy + offY,
        coreR * 0.1,
        cx + offX, cy + offY,
        coreR * 2.2
      );
      g3.addColorStop(0, p.core);
      g3.addColorStop(0.4, p.core + "aa");
      g3.addColorStop(1, "transparent");
      ctx.fillStyle = g3;
      ctx.beginPath();
      ctx.arc(cx + offX, cy + offY, coreR * 2.2, 0, Math.PI * 2);
      ctx.fill();

      /* ---------- Analysis geometry (subtle hex) ---------- */
      if (p.geom) {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(t * 0.12);
        ctx.strokeStyle = "rgba(124,196,255,0.18)";
        ctx.lineWidth = 0.8;
        for (let i = 0; i < 6; i++) {
          ctx.beginPath();
          ctx.moveTo(0, 0);
          const a = (i / 6) * Math.PI * 2;
          ctx.lineTo(Math.cos(a) * size * 0.2, Math.sin(a) * size * 0.2);
          ctx.stroke();
        }
        ctx.restore();
      }

      /* ---------- Orbital ring particles ---------- */
      const orbitBase = size * 0.5;
      orbital.forEach((o) => {
        const a = t * o.sp + o.ph;
        const targetR = orbitBase * o.rRel * (1 - contract * 0.4);
        const wob = Math.sin(t * 0.7 + o.ph) * 0.04;
        const r = targetR * (1 + wob);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r * 0.62;
        ctx.globalAlpha = 0.18 + 0.5 * o.depth;
        ctx.fillStyle = p.ring2;
        ctx.beginPath();
        ctx.arc(x, y, o.sz, 0, Math.PI * 2);
        ctx.fill();
      });

      /* ---------- Free-floating specks (deeper field) ---------- */
      drift.forEach((d) => {
        const ph = d.ph + t * d.sp;
        const x = ((d.x + Math.sin(ph) * 0.02) % 1) * size;
        const y = ((d.y + Math.cos(ph * 1.1) * 0.018) % 1) * size;
        // discard specks too close to outer halo to keep depth
        const dx = x - cx, dy = y - cy;
        if (Math.hypot(dx, dy) < size * 0.18) return;
        ctx.globalAlpha = 0.06 + 0.20 * d.depth;
        ctx.fillStyle = p.ring2;
        ctx.beginPath();
        ctx.arc(x, y, d.sz, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;

      /* ---------- Speaking — slow expanding waves ---------- */
      if (state === "speaking") {
        for (let i = 0; i < 3; i++) {
          const wt = ((t * 0.6) + i * 0.33) % 1;
          const wr = size * 0.18 + wt * size * 0.30;
          ctx.strokeStyle = `rgba(246, 214, 138, ${0.18 * (1 - wt)})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.arc(cx, cy, wr, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [state, level, size, profile]);

  return (
    <canvas
      ref={ref}
      className={`pointer-events-none select-none ${className}`}
      data-testid="living-orb"
      data-state={state}
      aria-hidden="true"
      style={{ filter: "drop-shadow(0 0 60px rgba(60,124,220,0.35))" }}
    />
  );
}
